import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Table, Button, Card, Space, Tag, Input, Select, message, Avatar
} from 'antd';
import {
  EyeOutlined, EditOutlined, SearchOutlined, ShopOutlined, DownloadOutlined, StarOutlined, TrophyOutlined
} from '@ant-design/icons';
import { exportToExcel } from '../../utils/excelExport';
import { useStores } from '../../stores';
import EditPartnerModal from '../../components/EditPartnerModal';
import BackButton from '../../components/BackButton';

const { Search } = Input;

const PartnerList = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { adminPartnerStore } = useStores();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);

  const [filters, setFilters] = useState({
    is_verified: null,
    segment: null,
    search: ''
  });
  const [updatingSegment, setUpdatingSegment] = useState(null);

  useEffect(() => {
    loadPartners();
  }, []);

  const loadPartners = async () => {
    try {
      const params = {};
      if (filters.is_verified !== null) params.is_verified = filters.is_verified;
      if (filters.segment) params.segment = filters.segment;
      if (filters.search) params.search = filters.search;

      await adminPartnerStore.fetchAllPartners(params);
    } catch (error) {
      message.error(t('admin.partners.loadFailed'));
    }
  };

  const handleEdit = (partner) => {
    setSelectedPartner(partner);
    setEditModalVisible(true);
  };

  const handleUpdateSubmit = async (partnerId, updateData, isFormData = false) => {
    await adminPartnerStore.updatePartner(partnerId, updateData, isFormData);
    await loadPartners(); // Reload partners to show updates
  };

  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
  };

  const handleVerificationFilter = (value) => {
    setFilters(prev => ({ ...prev, is_verified: value }));
  };

  const handleSegmentFilter = (value) => {
    setFilters(prev => ({ ...prev, segment: value }));
  };

  const handleSegmentChange = async (partnerId, newSegment) => {
    setUpdatingSegment(partnerId);
    try {
      await adminPartnerStore.updatePartner(partnerId, { segment: newSegment });
      message.success(t('admin.partners.segmentUpdated'));
      loadPartners();
    } catch (error) {
      message.error(t('admin.partners.segmentUpdateFailed'));
    } finally {
      setUpdatingSegment(null);
    }
  };

  const handleExport = () => {
    const exportColumns = [
      { title: 'ID', dataIndex: 'id' },
      { title: t('admin.partners.storeName'), dataIndex: 'store_name' },
      { title: t('admin.partners.taxNumber'), dataIndex: 'tax_number' },
      { title: t('admin.partners.platformFee'), dataIndex: 'platform_fee_percentage' },
      { title: t('admin.partners.segment'), dataIndex: 'segment' },
      { title: t('common.city'), dataIndex: 'city' },
      { title: t('common.address'), dataIndex: 'address' },
      { title: t('common.postalCode'), dataIndex: 'postal_code' },
      {
        title: t('common.status'),
        dataIndex: 'is_verified',
        exportRender: (val) => val ? t('admin.partners.verified') : t('common.pending')
      },
      {
        title: t('admin.partners.impactPartner'),
        dataIndex: 'is_impact_partner',
        exportRender: (val) => val ? t('common.yes') : t('common.no')
      }
    ];
    exportToExcel(adminPartnerStore.partners, exportColumns, 'partners', t('admin.partners.title'));
    message.success(t('admin.partners.exportSuccess'));
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      fixed: 'left',
    },
    {
      title: t('admin.partners.logo'),
      dataIndex: 'logo',
      key: 'logo',
      width: 60,
      render: (logo, record) => (
        logo?.thumbnail ? (
          <Avatar size={36} src={logo.thumbnail} />
        ) : (
          <Avatar size={36} icon={<ShopOutlined />} style={{ backgroundColor: '#ff7a45' }} />
        )
      ),
    },
    {
      title: t('admin.partners.storeName'),
      dataIndex: 'store_name',
      key: 'store_name',
      width: 180,
      ellipsis: true,
    },
    {
      title: t('admin.partners.taxNumber'),
      dataIndex: 'tax_number',
      key: 'tax_number',
      width: 120,
      ellipsis: true,
    },
    {
      title: t('admin.partners.platformFee'),
      dataIndex: 'platform_fee_percentage',
      key: 'platform_fee_percentage',
      width: 80,
      render: (fee) => `${fee}%`,
    },
    {
      title: t('common.city'),
      dataIndex: 'city',
      key: 'city',
      width: 120,
      ellipsis: true,
    },
    {
      title: t('common.status'),
      dataIndex: 'is_verified',
      key: 'is_verified',
      width: 100,
      render: (is_verified) => (
        <Tag color={is_verified ? 'green' : 'orange'}>
          {is_verified ? t('admin.partners.verified') : t('common.pending')}
        </Tag>
      ),
    },
    {
      title: t('admin.partners.segment'),
      dataIndex: 'segment',
      key: 'segment',
      width: 140,
      render: (segment, record) => (
        <Select
          value={segment || 'NONE'}
          onChange={(value) => handleSegmentChange(record.id, value)}
          loading={updatingSegment === record.id}
          size="small"
          style={{ width: 120 }}
        >
          <Select.Option value="FOUNDING">
            <TrophyOutlined style={{ color: '#52c41a' }} /> {t('admin.partners.segments.founding')}
          </Select.Option>
          <Select.Option value="VIP">
            <StarOutlined style={{ color: 'gold' }} /> VIP
          </Select.Option>
          <Select.Option value="MEDIUM">{t('admin.partners.segments.medium')}</Select.Option>
          <Select.Option value="LOW">{t('admin.partners.segments.low')}</Select.Option>
          <Select.Option value="NONE">{t('admin.partners.segments.none')}</Select.Option>
        </Select>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 160,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/admin/partners/${record.id}`)}
          >
            {t('common.view')}
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            {t('common.edit')}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <BackButton to="/admin/dashboard" label={t('admin.backToDashboard')} />
      <Card
        title={
          <Space>
            <ShopOutlined style={{ fontSize: '20px' }} />
            <span style={{ fontSize: '20px', fontWeight: 'bold' }}>
              {t('admin.partners.title')}
            </span>
            <Tag color="purple">{t('admin.partners.count', { count: adminPartnerStore.partners.length })}</Tag>
          </Space>
        }
      >
        {/* Filters */}
        <Space style={{ marginBottom: 16 }} size="middle">
          <Search
            placeholder={t('admin.partners.searchPlaceholder')}
            onSearch={handleSearch}
            onChange={(e) => !e.target.value && handleSearch('')}
            style={{ width: 300 }}
            prefix={<SearchOutlined />}
          />

          <Select
            placeholder={t('admin.partners.filterByStatus')}
            style={{ width: 180 }}
            allowClear
            value={filters.is_verified}
            onChange={handleVerificationFilter}
          >
            <Select.Option value={true}>{t('admin.partners.verified')}</Select.Option>
            <Select.Option value={false}>{t('common.pending')}</Select.Option>
          </Select>

          <Select
            placeholder={t('admin.partners.filterBySegment')}
            style={{ width: 170 }}
            allowClear
            value={filters.segment}
            onChange={handleSegmentFilter}
          >
            <Select.Option value="FOUNDING"><TrophyOutlined style={{ color: '#52c41a' }} /> {t('admin.partners.segments.founding')}</Select.Option>
            <Select.Option value="VIP"><StarOutlined style={{ color: 'gold' }} /> VIP</Select.Option>
            <Select.Option value="MEDIUM">{t('admin.partners.segments.medium')}</Select.Option>
            <Select.Option value="LOW">{t('admin.partners.segments.low')}</Select.Option>
            <Select.Option value="NONE">{t('admin.partners.segments.none')}</Select.Option>
          </Select>

          <Button type="primary" onClick={loadPartners}>
            {t('admin.applyFilters')}
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExport}
            disabled={!adminPartnerStore.partners.length}
          >
            {t('admin.exportExcel')}
          </Button>
        </Space>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={adminPartnerStore.partners}
          rowKey="id"
          loading={adminPartnerStore.loading}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1100 }}
        />
      </Card>

      {/* Edit Partner Modal */}
      <EditPartnerModal
        visible={editModalVisible}
        partner={selectedPartner}
        onClose={() => {
          setEditModalVisible(false);
          setSelectedPartner(null);
        }}
        onUpdate={handleUpdateSubmit}
      />
    </div>
  );
});

export default PartnerList;
